import { Component, OnInit, AfterViewInit, ElementRef, ViewChild, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { gsap } from 'gsap';
import { ScrollTrigger, CSSPlugin } from 'gsap/all';
import { LocationService } from '../../services/location.service';

let heroPluginsRegistered = false;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DC','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

@Component({
  selector: 'app-hero-section',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './hero-section.component.html',
  styleUrl: './hero-section.component.scss'
})
export class HeroSectionComponent implements OnInit, AfterViewInit {
  @ViewChild('heroSection', { static: true }) heroSection!: ElementRef<HTMLElement>;

  private readonly isBrowser: boolean;

  readonly states = US_STATES;

  searchQuery = '';
  locationCity = '';
  locationState = '';
  isDetecting = false;
  locationError = '';

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private readonly locationService: LocationService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser && !heroPluginsRegistered) {
      gsap.registerPlugin(ScrollTrigger, CSSPlugin);
      heroPluginsRegistered = true;
    }
  }

  ngOnInit(): void {
    // Pre-populate from stored location
    const stored = this.locationService.snapshot;
    if (stored) {
      this.locationCity = stored.city;
      this.locationState = stored.state;
    }
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;
    if (window.matchMedia('(max-width: 768px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.initializeAnimations();
  }

  initializeAnimations(): void {
    if (!this.isBrowser) {
      return;
    }

    gsap.to('.dancer', { y: -20, duration: 3, ease: 'power1.inOut', stagger: 0.3, yoyo: true, repeat: -1 });
    gsap.to('.note', { y: -30, rotation: 360, duration: 4, ease: 'power1.inOut', stagger: 0.5, yoyo: true, repeat: -1 });
    gsap.to('.slogan-text', { scale: 1.05, duration: 2, ease: 'power1.inOut', yoyo: true, repeat: -1 });

    gsap.to('.floating-dancers, .music-notes', {
      yPercent: -50,
      ease: 'none',
      scrollTrigger: {
        trigger: this.heroSection.nativeElement,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  }

  async onSearch(): Promise<void> {
    if (!this.isBrowser) return;

    this.locationError = '';

    if (this.locationCity.trim() && this.locationState.trim()) {
      const result = await this.locationService.setManualLocation(
        this.locationCity.trim(),
        this.locationState.trim(),
      );
      if (!result) {
        this.locationError = 'Could not find that location. Please try a different city.';
      }
    }
  }

  async detectLocation(): Promise<void> {
    if (!this.isBrowser) return;

    this.isDetecting = true;
    this.locationError = '';

    const result = await this.locationService.detectFromBrowser();

    this.isDetecting = false;

    if (result) {
      this.locationCity = result.city;
      this.locationState = result.state;
    } else {
      this.locationError = 'Could not detect your location. Please enter it manually.';
    }
  }
}
